// features/profile/edit/EditProfileSidebar.tsx — the Edit Profile page's sub-nav
// (1.27.70, owner concept edit_profile_v2). Each item jumps to its card on the
// same page; the active one follows the scroll (EditProfilePage owns that).
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Icon } from '@/icons';
import { usePremium, useSession } from '@/hooks';
import { softControlMotion } from '@/theme/interactions';
import { EDIT_SECTIONS } from './editSections';

const Aside = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 100%;
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  line-height: ${({ theme }) => theme.lineHeight.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Blurb = styled.p`
  margin: 4px 0 12px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Item = styled.a<{ $active: boolean }>`
  ${softControlMotion}
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  text-decoration: none;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight.sm};
  color: ${({ theme, $active }) => ($active ? theme.color.textPrimary : theme.color.textMuted)};
  background: ${({ theme, $active }) => ($active ? theme.color.surface : 'transparent')};
  font-weight: ${({ $active }) => ($active ? 600 : 'inherit')};
  &:hover {
    background: ${({ theme }) => theme.color.glassBg};
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
  }
`;

const Rule = styled.hr`
  width: 100%;
  margin: 10px 0;
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
`;

const Back = styled(Link)`
  ${softControlMotion}
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  text-decoration: none;
  &:hover {
    background: ${({ theme }) => theme.color.glassBg};
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
  }
`;

const Promo = styled.div`
  margin-top: auto;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PromoHead = styled.p`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  > svg {
    color: ${({ theme }) => theme.color.supporter};
  }
`;

const PromoText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: ${({ theme }) => theme.lineHeight.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const PromoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  text-decoration: none;
  color: ${({ theme }) => theme.color.accentCyan};
  background: color-mix(in srgb, ${({ theme }) => theme.color.accentCyan} 14%, transparent);
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
  }
`;

export interface EditProfileSidebarProps {
  active: string;
  onJump: (id: string) => void;
}

export function EditProfileSidebar({ active, onJump }: EditProfileSidebarProps) {
  const { isPremium } = useSession();
  const { tiers } = usePremium();
  const pro = tiers.find((t) => t.id === 'pro');
  return (
    <Aside aria-label="Edit profile sections">
      <Title>Edit Profile</Title>
      <Blurb>Update your information and control how you appear on TruXSocial.</Blurb>
      {EDIT_SECTIONS.map((s) => (
        <Item
          key={s.id}
          href={`#${s.id}`}
          $active={s.id === active}
          aria-current={s.id === active ? 'location' : undefined}
          onClick={(e) => {
            e.preventDefault();
            onJump(s.id);
          }}
        >
          <Icon name={s.icon} size={18} />
          {s.label}
        </Item>
      ))}
      <Rule />
      <Back to="/settings/account">
        <Icon name="arrow-left" size={18} />
        Back to Settings
      </Back>
      <Promo>
        <PromoHead>
          <Icon name="crown" size={18} />
          TruXSocial Premium
        </PromoHead>
        {/* Copy from core/data/premium.ts: the Pro tagline and its last promise. */}
        {pro && (
          <PromoText>
            {pro.tagline}. {pro.benefits[pro.benefits.length - 1]}.
          </PromoText>
        )}
        <PromoLink to="/premium">
          {isPremium ? 'Manage Premium' : 'Upgrade now'}
          <Icon name="arrow-right" size={14} />
        </PromoLink>
      </Promo>
    </Aside>
  );
}
